import type { Schemas } from "@platform/contracts/client";
import {
  Button,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  StatusBadge,
  statusTone,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from "@platform/ui";
import { ErrorText, errorKey, localName, useSession } from "@platform/ui/features";
import { PackageSearch } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslations } from "use-intl";
import { formatDateTime, locale } from "../lib/i18n";

type Item = Schemas["OpsProductListItem"];
type Status = Item["status"];

const STATUSES: readonly Status[] = ["draft", "pending_review", "live", "off_shelf", "blocked"];
const MIN_REASON = 3;

/** Blocking or unblocking one product, always with a reason the seller receives (CAT-4). */
function BlockForm({ item, onDone }: { item: Item; onDone: (item: Item) => void }) {
  const t = useTranslations();
  const { session } = useSession();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const unblock = item.status === "blocked";
  const id = `reason-${item.id}`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const path = unblock ? "/v1/ops/products/{productId}/unblock" : "/v1/ops/products/{productId}/block";
    const { data, error: problem } = await session.api.POST(path, {
      params: { path: { productId: item.id } },
      body: { reason: reason.trim() },
    });
    setBusy(false);
    if (data === undefined) return setError(t(errorKey(problem)));
    onDone({ ...item, ...data });
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={(event) => void submit(event)}>
      <Label htmlFor={id}>{t(unblock ? "admin.products.unblockReason" : "admin.products.blockReason")}</Label>
      <Textarea
        id={id}
        rows={3}
        value={reason}
        maxLength={2000}
        onChange={(event) => setReason(event.target.value)}
      />
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      <Button
        type="submit"
        size="sm"
        className="self-start"
        disabled={busy || reason.trim().length < MIN_REASON}
      >
        {t(unblock ? "admin.products.unblock" : "admin.products.block")}
      </Button>
    </form>
  );
}

/** Products looked up by name, slug, or shop, to block or unblock (CAT-4, card 044). */
export function ProductsPage() {
  const t = useTranslations();
  const { session } = useSession();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<Status | "">("");
  const [items, setItems] = useState<readonly Item[] | undefined>();
  const [open, setOpen] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const search = async (event?: FormEvent) => {
    event?.preventDefault();
    const { data, error: problem } = await session.api.GET("/v1/ops/products", {
      params: {
        query: { ...(q.trim() === "" ? {} : { q: q.trim() }), ...(status === "" ? {} : { status }) },
      },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setError(undefined);
    setItems(data.items);
  };

  return (
    <section aria-labelledby="products-title" className="flex flex-col gap-5">
      <PageHeader
        id="products-title"
        title={t("admin.products.title")}
        description={t("admin.products.intro")}
      />
      <form
        className="flex flex-wrap items-end gap-3 rounded-(--radius-card) border border-border bg-surface p-4"
        onSubmit={(event) => void search(event)}
      >
        <div className="flex min-w-72 flex-1 flex-col gap-1.5">
          <Label htmlFor="products-q">{t("admin.products.search")}</Label>
          <Input id="products-q" value={q} maxLength={100} onChange={(event) => setQ(event.target.value)} />
        </div>
        <div className="flex w-48 flex-col gap-1.5">
          <Label htmlFor="products-status">{t("admin.products.statusFilter")}</Label>
          <Select
            id="products-status"
            value={status}
            onChange={(event) => setStatus(event.target.value as Status | "")}
          >
            <option value="">{t("admin.products.anyStatus")}</option>
            {STATUSES.map((each) => (
              <option key={each} value={each}>
                {t(`admin.products.status.${each}`)}
              </option>
            ))}
          </Select>
        </div>
        <Button type="submit">{t("admin.products.find")}</Button>
      </form>
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {items !== undefined && (
        <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
          {items.length === 0 ? (
            <EmptyState icon={PackageSearch} title={t("admin.products.none")} />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("admin.reviews.product")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.products.shop")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.reviews.status")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.products.changed")}</TableHeaderCell>
                  <TableHeaderCell>
                    <span className="sr-only">{t("admin.products.actions")}</span>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} className="align-top">
                    <TableCell>
                      <span className="font-medium text-ink">{localName(item.name, locale)}</span>
                      {item.slug !== null && (
                        <span className="block text-caption text-ink-muted">{item.slug}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {localName(item.shop.name, locale)}
                      <span className="block text-caption text-ink-muted">{item.shop.slug}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        label={t(`admin.products.status.${item.status}`)}
                        tone={statusTone("product", item.status)}
                      />
                      {item.block !== null && (
                        <span className="mt-1 block text-caption text-tone-problem-fg">
                          {t("admin.products.blockedBecause", { reason: item.block.reason })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">{formatDateTime(item.updatedAt)}</TableCell>
                    <TableCell className="w-72">
                      {(item.status === "live" || item.status === "off_shelf" || item.status === "blocked") &&
                        (open === item.id ? (
                          <BlockForm
                            item={item}
                            onDone={(next) => {
                              setItems((current) =>
                                current?.map((each) => (each.id === next.id ? next : each)),
                              );
                              setOpen(undefined);
                            }}
                          />
                        ) : (
                          <Button
                            size="sm"
                            variant={item.status === "blocked" ? "secondary" : "ghost"}
                            onClick={() => setOpen(item.id)}
                          >
                            {t(item.status === "blocked" ? "admin.products.unblock" : "admin.products.block")}
                          </Button>
                        ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </section>
  );
}
